import Flex from 'components/common/Flex';
import SubtleBadge from 'components/common/SubtleBadge';
import {
  account,
  generals,
  sla,
  team,
  ticket
} from 'data/support-desk/quickLinksData';
import { useOAuth2 } from 'hooks/useOAuth2';
import React from 'react';
import { Card, Col, Nav, Row } from 'react-bootstrap';
import { BsShieldCheck } from 'react-icons/bs';
import { FiLogOut } from 'react-icons/fi';
import {
  MdConfirmationNumber,
  MdPeople,
  MdPerson,
  MdSettings
} from 'react-icons/md';
import { Link } from 'react-router-dom';

const SettingsItem = ({ item, color }) => {
  return (
    <Col md={6} lg={6} xxl={4}>
      <Link to={item.to} className="text-decoration-none">
        <Flex
          alignItems="start"
          className="p-2 rounded-3 hover-bg-100 transition-base h-100"
        >
          <div className={`me-3 text-${color} fs-5`}>{item.icon}</div>
          <div className="flex-1">
            <Flex justifyContent="between" alignItems="center" className="mb-1">
              <h6 className="text-900 mb-0">{item.title}</h6>
              {item.isNew && (
                <SubtleBadge bg="success" className="ms-2">
                  BETA
                </SubtleBadge>
              )}
            </Flex>
            <p className="mb-0 text-600 fs--1 lh-sm">{item.description}</p>
          </div>
        </Flex>
      </Link>
    </Col>
  );
};

const LogoutSettingsItem = () => {
  const { resetOAuthState } = useOAuth2();
  const handleLogout = e => {
    e.preventDefault();
    resetOAuthState();
    window.location.href = `${process.env.REACT_APP_BASE_URL}/logout`;
  };

  return (
    <Col md={6} lg={6} xxl={4}>
      <a href="#" onClick={handleLogout} className="text-decoration-none">
        <Flex
          alignItems="start"
          className="p-2 rounded-3 hover-bg-100 transition-base h-100"
        >
          <div className="me-3 text-warning fs-5">
            <FiLogOut />
          </div>
          <div className="flex-1">
            <h6 className="text-900 mb-1">Logout</h6>
            <p className="mb-0 text-600 fs--1 lh-sm">Fazer logout</p>
          </div>
        </Flex>
      </a>
    </Col>
  );
};

const Settings = () => {
  const sections = [
    {
      id: 'team',
      title: 'Equipe',
      icon: <MdPeople />,
      data: team,
      description:
        'Defina os níveis de acesso dos agentes e o horário de trabalho',
      color: 'info'
    },
    {
      id: 'ticket',
      title: 'Tickets',
      icon: <MdConfirmationNumber />,
      data: ticket,
      description: 'Configure o processo de roteamento e resolução de tickets',
      color: 'primary'
    },
    {
      id: 'sla',
      title: 'Configurações de SLA',
      icon: <BsShieldCheck />,
      data: sla,
      description: 'Estabeleça SLAs e responsabilize os agentes',
      color: 'warning'
    },
    {
      id: 'generals',
      title: 'Geral',
      icon: <MdSettings />,
      data: generals,
      description: 'Mapeie e gerencie toda a sua estrutura de suporte',
      color: 'success'
    },
    {
      id: 'account',
      title: 'Conta',
      icon: <MdPerson />,
      data: account,
      description: 'Gerencie suas informações de faturamento e conta',
      color: 'secondary'
    }
  ];

  const getColoredItem = (item, color) => {
    return {
      ...item,
      color: color,
      icon: React.cloneElement(item.icon, {
        className: item.icon.props.className
          .replace(/text-\w+/, `text-${color}`)
          .replace(/fs-\d+/, '')
      })
    };
  };

  return (
    <Card>
      <Card.Body>
        <Row>
          <Col lg={3} className="border-end px-0">
            <div className="sticky-top py-3" style={{ top: '5rem' }}>
              <h5 className="mb-4 ps-2">Configurações</h5>
              <Nav className="flex-column">
                {sections.map(section => (
                  <Nav.Link
                    href={`#${section.id}`}
                    key={section.id}
                    className="text-800 px-2 py-4 mb-1 rounded-2 hover-bg-100 transition-base"
                  >
                    <Flex direction="column">
                      <Flex alignItems="center">
                        <span className={`me-2 text-${section.color} fs-7`}>
                          {section.icon}
                        </span>
                        <span className="fw-medium">{section.title}</span>
                      </Flex>
                      <small className="text-500 d-block">
                        {section.description}
                      </small>
                    </Flex>
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </Col>
          <Col lg={9} className="pt-1">
            <div className="p-3">
              {sections.map((section, index) => (
                <div
                  id={section.id}
                  key={section.id}
                  className={index !== sections.length - 1 ? 'mb-6' : 'mb-3'}
                  style={{ scrollMarginTop: '6rem' }}
                >
                  <Flex alignItems="center" className="mb-4">
                    <span className={`text-${section.color} fs-5 me-2`}>
                      {section.icon}
                    </span>
                    <div>
                      <h5 className="mb-0">{section.title}</h5>
                      <p className="mb-0 text-600">{section.description}</p>
                    </div>
                  </Flex>

                  <Row className="g-3">
                    {section.id === 'account' ? (
                      <>
                        {section.data
                          .filter(item => !item.to?.includes('/logout'))
                          .map((item, i) => (
                            <SettingsItem
                              key={i}
                              item={getColoredItem(item, section.color)}
                              color={section.color}
                            />
                          ))}
                        <LogoutSettingsItem />
                      </>
                    ) : (
                      section.data.map((item, i) => (
                        <SettingsItem
                          key={i}
                          item={getColoredItem(item, section.color)}
                          color={section.color}
                        />
                      ))
                    )}
                  </Row>

                  {index !== sections.length - 1 && (
                    <hr className="mt-5 mb-0 text-200" />
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default Settings;
